import React, { useEffect, useMemo, useRef } from 'react';
import { useOcean } from '../../contexts/OceanDataContext';
import {
  SCENARIO_COMMS_SCRIPT,
  SCENARIO_COMMS_CHANNEL_STYLES,
  scenarioCommsMaxFullyPassedMessageIndex
} from '../../data/scenarioCommsData';
import { frameIndexForStoryTime, VESSEL_MAP_PLAYBACK_VIEW_MODES } from '../../utils/storyTimeUtils';

const DEFAULT_CHANNEL = SCENARIO_COMMS_CHANNEL_STYLES['moc-command'];

const CommsMessageRow = ({ msg, text, msgIndex, styles, stillStreaming, isPlaying, embedded }) => {
  if (embedded) {
    const accent = styles.embedded || DEFAULT_CHANNEL.embedded;
    return (
      <article
        className={`rounded-md border border-slate-200 bg-white px-3 py-2 shadow-sm border-l-[3px] ${accent.border} ${
          stillStreaming ? 'ring-1 ring-amber-300/80' : ''
        }`}
      >
        <div className="mb-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${accent.badge}`}>
            {msg.narrativePhase}
          </span>
          <span className={`text-[10px] font-medium ${accent.route}`}>{styles.label}</span>
        </div>
        <div className="mb-1 text-[11px] leading-tight text-slate-600">
          <span className="font-semibold text-slate-800">{msg.from}</span>
          <span className="mx-1 text-slate-400" aria-hidden>→</span>
          <span className="font-semibold text-slate-800">{msg.to}</span>
        </div>
        <p className="text-[13px] leading-relaxed text-slate-900 whitespace-pre-wrap">
          {text}
          {stillStreaming ? (
            <span
              className={`inline-block w-0.5 h-3.5 ml-0.5 align-middle bg-amber-500 opacity-80${isPlaying ? ' animate-pulse' : ''}`}
              aria-hidden
            />
          ) : null}
        </p>
      </article>
    );
  }

  return (
    <article
      className={`rounded-lg border border-slate-200/80 dark:border-slate-600/50 pl-2 pr-2 py-2 text-xs ${styles.bar}`}
    >
      <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
        <span>{msg.narrativePhase}</span>
        <span aria-hidden>·</span>
        <span>{styles.label}</span>
      </div>
      <div className="text-[10px] text-slate-500 dark:text-slate-400 mb-1">
        <span className="font-medium text-slate-700 dark:text-slate-300">{msg.from}</span>
        <span> → </span>
        <span className="font-medium text-slate-700 dark:text-slate-300">{msg.to}</span>
      </div>
      <p className="text-slate-800 dark:text-slate-200 leading-snug whitespace-pre-wrap">
        {text}
        {stillStreaming ? (
          <span
            className={`inline-block w-0.5 h-3 ml-0.5 align-middle bg-current opacity-70${isPlaying ? ' animate-pulse' : ''}`}
            aria-hidden
          />
        ) : null}
      </p>
    </article>
  );
};

/**
 * Exercise comms: lines follow story time (scrubber / playback). Completed beats show full text;
 * the active beat streams from `scenarioCommsBurst` when it matches the current step.
 */
const ExerciseCommsPanel = ({ embedded = false }) => {
  const {
    scenarioCommsBurst,
    mapLayerVisibility,
    isPlaying,
    scenarioCommsPlaybackStep,
    vesselPlaybackTime,
    vesselTimeRange,
    viewMode,
    storySeries
  } = useOcean();
  const exerciseOn = Boolean(mapLayerVisibility?.exerciseScenario);
  const logEndRef = useRef(null);

  const uNorm = useMemo(() => {
    if (!exerciseOn) return null;
    if (vesselPlaybackTime == null) return null;
    if (VESSEL_MAP_PLAYBACK_VIEW_MODES.has(viewMode) && storySeries.length > 0) {
      const idx = frameIndexForStoryTime(vesselPlaybackTime, storySeries);
      const n = storySeries.length;
      return n <= 1 ? 0 : idx / Math.max(1, n - 1);
    }
    if (!vesselTimeRange) return null;
    const span = Math.max(1, vesselTimeRange.maxMs - vesselTimeRange.minMs);
    return (vesselPlaybackTime - vesselTimeRange.minMs) / span;
  }, [exerciseOn, vesselPlaybackTime, viewMode, storySeries, vesselTimeRange]);

  const lines = useMemo(() => {
    if (!exerciseOn || uNorm == null) return [];
    const maxPassed = scenarioCommsMaxFullyPassedMessageIndex(uNorm);
    const step = scenarioCommsPlaybackStep;
    const activeIdx = step > 0 ? step - 1 : -1;
    const rows = [];
    for (let j = 0; j <= maxPassed; j++) {
      const msg = SCENARIO_COMMS_SCRIPT[j];
      rows.push({ msgIndex: j, msg, text: msg.text, phase: 'done' });
    }
    if (activeIdx >= 0 && activeIdx > maxPassed) {
      const msg = SCENARIO_COMMS_SCRIPT[activeIdx];
      const b = scenarioCommsBurst;
      if (b && b.msgIndex === activeIdx) {
        const text = b.phase === 'stream' ? msg.text.slice(0, b.displayedLen) : msg.text;
        rows.push({ msgIndex: activeIdx, msg, text, phase: b.phase });
      } else {
        rows.push({ msgIndex: activeIdx, msg, text: msg.text, phase: 'done' });
      }
    }
    return rows;
  }, [exerciseOn, uNorm, scenarioCommsPlaybackStep, scenarioCommsBurst]);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [lines]);

  const statusLine = !exerciseOn
    ? (
      <>
        Turn on the <span className="font-medium text-slate-700">Exercise scenario</span> map layer to
        open the live circuit.
      </>
    )
    : lines.length > 0
      ? `Live circuit · ${lines.length} message${lines.length === 1 ? '' : 's'} — scrub back to hide later traffic`
      : 'Live circuit — start playback or scrub into the exercise timeline';

  return (
    <div className={`h-full flex flex-col min-h-0 ${embedded ? 'bg-transparent' : 'bg-white/50 dark:bg-slate-900/50'}`}>
      <div className={`flex-shrink-0 border-b border-slate-200 px-3 py-1.5 text-[11px] leading-snug text-slate-500 ${
        embedded ? 'bg-slate-50/80' : 'dark:border-slate-700 dark:text-slate-400'
      }`}>
        {statusLine}
      </div>
      <div className={`flex-1 min-h-0 overflow-y-auto ${embedded ? 'p-3 space-y-1.5' : 'p-2 space-y-2'}`}>
        {lines.length === 0 ? (
          <div className="text-center text-slate-400 text-xs py-8 px-3">
            {exerciseOn
              ? 'No traffic yet. Comms appear at threat posture, detection, convergence, and recovery.'
              : 'Enable the exercise layer to use this channel.'}
          </div>
        ) : (
          lines.map((line) => {
            const { msg, text, msgIndex } = line;
            const styles = SCENARIO_COMMS_CHANNEL_STYLES[msg.channel] || DEFAULT_CHANNEL;
            const stillStreaming =
              scenarioCommsBurst?.msgIndex === msgIndex &&
              scenarioCommsBurst.phase === 'stream' &&
              scenarioCommsBurst.displayedLen < msg.text.length;
            return (
              <CommsMessageRow
                key={msgIndex}
                msg={msg}
                text={text}
                msgIndex={msgIndex}
                styles={styles}
                stillStreaming={stillStreaming}
                isPlaying={isPlaying}
                embedded={embedded}
              />
            );
          })
        )}
        <div ref={logEndRef} />
      </div>
      <div className={`flex-shrink-0 px-3 py-2 text-[10px] text-slate-500 border-t border-slate-200 ${
        embedded ? 'bg-white' : 'dark:border-slate-700 dark:text-slate-400'
      }`}>
        <a
          href={`${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/exercise-comms-transcript.txt`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-violet-700 underline hover:no-underline font-medium"
        >
          Open full client review transcript (.txt)
        </a>
        <span className="text-slate-400"> — same script as live comms</span>
      </div>
    </div>
  );
};

export default ExerciseCommsPanel;
